import { gql } from '@apollo/client';
import { GetServerSideProps, NextPage } from 'next';
import Link from 'next/link';
import { isMobileOnly } from 'react-device-detect';
import styled from 'styled-components';

import { FOOTER_QUERY } from '../../components/footer';
import { Footer } from '../../components/footer';
import { Header, HEADER_QUERY } from '../../components/header';
import LegalMobileMenu from '../../components/legal/mobileMenu';
import { Col, ResponsiveContainer, Row } from '../../components/responsive';
import { ContentStackClient } from '../../lib/contentstack';
import {
  AllPrivacyAndLegal,
  LegalContent,
  PrivacyAndLegal,
} from '../../types/contentstack';
import { IPageProps } from '../../types/page';
import { classnames } from '../../utils/classnames';
import { dateAndTime } from '../../utils/dateAndTime';
import { mediaScreen } from '../../utils/mediaScreen';

export const Page: NextPage<
  IPageProps<LegalContent> & { leftMenuItems: PrivacyAndLegal[] }
> = ({ header, page, footer, leftMenuItems }) => {
  return (
    <>
      <Header
        pageTitle={page?.title}
        disableHiding={!isMobileOnly}
        {...header}
      />

      <Container>
        <Row>
          <Col xs={12} md={3}>
            <LegalMobileMenu
              url="/legal/CustomerAgreement"
              leftMenuItems={leftMenuItems}
            />

            <LeftMenu>
              {leftMenuItems?.map((item, index) => (
                <Link href={item.url as string} key={`left-menu-item-${index}`}>
                  <span
                    className={classnames({
                      link: true,
                      active: item.url === '/legal/CustomerAgreement',
                    })}
                  >
                    {item.title}
                  </span>
                </Link>
              ))}
            </LeftMenu>
          </Col>

          <Col xs={12} md={8}>
            <Title>{page?.title}</Title>

            <HeaderContent>
              Last Updated: {dateAndTime(page?.update_date, 'long')}
              <Content
                dangerouslySetInnerHTML={{
                  __html: page?.header_content as string,
                }}
              />
            </HeaderContent>

            {!!page?.customer_agreementConnection?.edges?.length &&
              !!page?.customer_agreementConnection?.edges[0]?.node?.url && (
                <DownloadContent>
                  Download the Agreement{' '}
                  <a
                    target="_blank"
                    href={
                      page?.customer_agreementConnection?.edges[0]?.node?.url
                    }
                  >
                    here
                  </a>
                  .
                </DownloadContent>
              )}

            <TableOfContentsContent>
              <NonLegal
                dangerouslySetInnerHTML={{
                  __html: page?.non_legal_intro as string,
                }}
              />

              <Content
                dangerouslySetInnerHTML={{
                  __html: page?.legal_intro as string,
                }}
              />
            </TableOfContentsContent>

            <TitleTableOfContents>
              {page?.table_of_content_title}
            </TitleTableOfContents>

            <TableOfContentsList>
              {page?.legal_contents?.length &&
                page?.legal_contents.map((content, index) => (
                  <li key={`legal-contents-${index}`}>
                    <a href={`#${content?.content_section_id || index}`}>
                      {content?.legal_content_title}
                    </a>
                  </li>
                ))}
            </TableOfContentsList>

            {page?.legal_contents?.length &&
              page?.legal_contents.map((content, index) => (
                <TableOfContentsContent
                  key={`table-of-contents-${index}`}
                  id={`${content?.content_section_id || index}`}
                >
                  <TitleTableOfContents>
                    {index + 1}. {content?.legal_content_title}
                  </TitleTableOfContents>

                  <NonLegal
                    dangerouslySetInnerHTML={{
                      __html: content?.non_legal_explanation as string,
                    }}
                  />

                  <Content
                    dangerouslySetInnerHTML={{
                      __html: content?.legal_explanation as string,
                    }}
                  />
                </TableOfContentsContent>
              ))}
          </Col>
        </Row>
      </Container>

      <Footer sections={footer?.sections} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async () => {
  const data = await fetchCustomerAgreement();
  const legalData = await fetchLegalData();

  const page = data?.page;

  const leftMenuItems =
    legalData?.page?.items?.map((item) => ({
      title: item?.title,
      url: item?.url,
    })) ?? [];

  if (!page) {
    return {
      props: {},
      redirect: {
        statusCode: 302,
        destination: leftMenuItems[0]?.url as string,
      },
    };
  }

  return {
    props: {
      ...data,
      leftMenuItems,
      page,
      path: 'CustomerAgreement',
    },
  };
};

export async function fetchCustomerAgreement() {
  try {
    const { data } = await ContentStackClient().query<IPageProps<LegalContent>>(
      {
        query: PAGES_QUERY,
      }
    );

    return data;
  } catch (e) {
    return;
  }
}

export async function fetchLegalData() {
  try {
    const { data } = await ContentStackClient().query<
      IPageProps<AllPrivacyAndLegal>
    >({
      query: MENU_QUERY,
    });

    return data;
  } catch (e) {
    return;
  }
}

const MENU_QUERY = gql`
  query privacyAndLegalQuery {
    page: all_privacy_and_legal {
      items {
        url
        title
        content
      }
    }
  }
`;

const PAGES_QUERY = gql`
  query legalQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: legal_content(uid: "blt68aa9ec5d733b2ea") {
      title
      update_date
      header_content
      non_legal_intro
      legal_intro
      table_of_content_title
      customer_agreementConnection {
        edges {
          node {
            title
            url
            content_type
            filename
            file_size
            description
          }
        }
      }
      legal_contents {
        content_section_id
        legal_content_title
        non_legal_explanation
        legal_explanation
      }
    }
  }
`;

const Container = styled(ResponsiveContainer)`
  margin-top: 150px;
`;

const LeftMenu = styled.div`
  position: fixed;

  ${mediaScreen('xs', 'sm')} {
    display: none;
  }

  .link {
    color: white;
    font-size: 16px;
    font-weight: 400;
    margin-bottom: 15px;
    display: block;

    &:hover {
      text-decoration: underline;
    }

    &.active {
      font-weight: bold;
      color: ${(props) => props.theme.colors.teal};
    }
  }
`;

const Title = styled.h1`
  font-size: 50px;
  color: white;
  font-weight: bold;
  margin-bottom: 35px;
  line-height: 64px;
  margin-top: -5px;
`;

const HeaderContent = styled.div`
  padding-left: 30px;
  border-left: 5px solid ${(props) => props.theme.colors.teal};
`;

const Content = styled.div`
  text-align: justify;

  grid-row-start: 2;

  p:first-of-type {
    margin-top: 0;
  }

  * {
    font-size: 15px !important;
    font-family: inherit !important;
    color: white !important;
  }

  h1,
  h2,
  p {
    font-weight: 300;
    margin: 20px 0;
  }

  span > font {
    display: inline-block;
    width: 30px;
    overflow: hidden;
  }

  table {
    width: 100%;
    border-collapse: collapse !important;

    td {
      vertical-align: middle;
      text-align: center;
      padding: 10px !important;
      border-color: rgba(255, 255, 255, 0.4) !important;

      p {
        margin: 0;
      }
    }
  }
`;

const DownloadContent = styled.div`
  margin-top: 40px;

  a {
    color: ${(props) => props.theme.colors.teal};
  }
`;

const TitleTableOfContents = styled.h2`
  font-size: 38px;
  color: white;
  font-weight: bold;
  line-height: 64px;
  margin-top: 32px;

  grid-column-start: 2;
`;

const TableOfContentsList = styled.ol`
  list-style: auto;
  margin-left: 20px;

  li {
    margin: 12px 0;
    a {
      font-size: 16px;
      color: ${(props) => props.theme.colors.teal};
    }
  }
`;

const TableOfContentsContent = styled.div`
  padding-top: 92px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  grid-template-rows: 2;
  column-gap: 46px;

  ${mediaScreen('xs', 'sm')} {
    display: block;
  }
`;

const NonLegal = styled.span`
  text-align: justify;
  color: ${(props) => props.theme.colors.teal};
  font-size: 20px;

  grid-row-start: 2;

  p:first-of-type {
    margin-top: 0;
  }

  * {
    font-family: inherit !important;
  }

  h1,
  h2,
  p {
    font-weight: 300;
    margin: 20px 0;
  }

  span > font {
    display: inline-block;
    width: 30px;
    overflow: hidden;
  }

  table {
    width: 100%;
    border-collapse: collapse !important;

    td {
      vertical-align: middle;
      text-align: center;
      padding: 10px !important;
      border-color: rgba(255, 255, 255, 0.4) !important;

      p {
        margin: 0;
      }
    }
  }
`;

export default Page;
